import { H3, InformationH2 } from '@/components/atoms/typography'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import { validFor } from '@/theme/validFor'
import useTranslation from 'next-translate/useTranslation'
import styled, { useTheme } from 'styled-components'
import { lighten } from 'polished'
import { IconTab, LinkIconTab, TabWidth } from '@/components/molecules/tabs/tab'
import { FaqCategory, Page } from '@/network/graphql.g'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { requestThrow401 } from '@/utils/graphQl'
import { useMhub } from '@/providers/mhubProvider'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import { getMainData } from '@/utils/mainDataHelper'
import AccordionItem from '@/components/molecules/accordionItem'
import { TabPanel, Tabs } from '@/components/molecules/tabs'
import router from 'next/router'
import { Routes } from '@/core/routes'
import ContactService from '@/components/organisms/contact/contactService'
import { CheckOrderIconDefault } from '@/components/atoms/icons/CheckOrderIconDefault'
import useDevice from '../hooks/useDevice'
import { Box } from '@/components/atoms/Grid'

import { SeoTechnicalPage } from '@/utils/seoData'

type ContactProps = {
  faqCategories: FaqCategory[]
}

const Contact: FC<ContactProps> = ({ faqCategories }) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const { pushContactPage } = useMhub()
  useEffect(() => {
    pushContactPage()
  }, [])

  useSpeedCurve({
    label: 'Contact'
  })

  const [category, setCategory] = useState<string>()

  useEffect(() => {
    const match = router.asPath.match(/#tab-([0-9]+)/i)
    setCategory(
      match && match.length > 1 ? match[1] : faqCategories.find(() => true)?.id
    )
  }, [])

  const { isMobile } = useDevice()
  const faqQuestionRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <SeoTechnicalPage typePage="Contact"></SeoTechnicalPage>
      <PageContentWrapper
        mb={96}
        backgroundStyle={PageContentWrapperBackgroundStyle.normal}
      >
        <InformationH2>{t('Contact.title')}</InformationH2>
        <StyledH3>{t('Contact.faq')}</StyledH3>
        <Tabs
          value={category}
          setValue={setCategory}
          pt={0}
          tabWidth={TabWidth.FullWidth}
          underline={false}
        >
          {faqCategories
            .filter((faqCategory) => faqCategory.section === 'faq')
            .map((faqCategory) => (
              <StyledIconTab
                key={faqCategory.id}
                index={faqCategory.id}
                title={faqCategory.title}
                iconElement={
                  <StyledSpan
                    className={`fcicon ${faqCategory.icon}`}
                    selected={category === faqCategory.id}
                  />
                }
                scrollToFaq={() =>
                  isMobile &&
                  faqQuestionRef.current.scrollIntoView({
                    behavior: 'smooth'
                  })
                }
              />
            ))}
          <StyledCheckOrderLinkIconTab
            href={Routes.checkOrder}
            title={t('Contact.checkOrder')}
            iconElement={
              <CheckOrderIconDefault
                color={theme.colors.menuBackground}
                width={isMobile ? 18 : 28}
                height={isMobile ? 18 : 28}
              />
            }
          />
        </Tabs>

        <ContactService />

        <Box ref={faqQuestionRef} pt={{ mobile: '50px' }}>
          {faqCategories.map((faqCategory) => (
            <TabPanel
              key={faqCategory.id}
              index={faqCategory.id}
              value={category}
            >
              <H3>{faqCategory.title}</H3>
              {faqCategory.faqItems.map((faqItem, index) => (
                <React.Fragment key={JSON.stringify(faqItem)}>
                  {faqItem && faqCategory.faqItems.length - 1 !== index && (
                    <AccordionItem
                      title={faqItem.question}
                      description={faqItem.answer}
                      contentProps={{ pt: 24 }}
                    />
                  )}
                </React.Fragment>
              ))}
            </TabPanel>
          ))}
        </Box>
      </PageContentWrapper>
    </>
  )
}

export default Contact

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<ContactProps>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getFaqCategories,
      variables: {
        page: Page.Contact,
        withMainNavigation,
        withStore
      },
      mainResponse: (response) => response?.faqCategories
    })
    if (!success) {
      return {
        notFound: true
      }
    }
    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        faqCategories: response?.faqCategories
      }
    }
  }
)

const StyledCheckOrderLinkIconTab = styled(LinkIconTab)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.menuBackground};
  &:hover {
    background-color: ${({ theme }) => lighten(0.1, theme.colors.accent)};
  }
`

const StyledSpan = styled.span`
  font-size: 28px;
  ${validFor.mobile`
    font-size: 18px;
  `}
`

const StyledH3 = styled(H3)`
  text-align: center;
  text-transform: uppercase;
  ${validFor.tablet`
    font-size: 16px;
  `}
  ${validFor.mobile`
    font-size: 16px;
  `}
`

const StyledIconTab = styled(IconTab)`
  && {
    background-color: ${({ index, value, theme }) =>
      index === value
        ? lighten(0.42, theme.colors.accent)
        : lighten(0.93, theme.colors.onBackground)};
  }
  &&:hover {
    background-color: ${({ index, value, theme }) =>
      index === value
        ? lighten(0.42, theme.colors.accent)
        : lighten(0.93, theme.colors.onBackground)} !important;
  }
`
